import { useAuth } from '@/contexts/AuthContext'
import { type PlanId } from '@/lib/pricing'
import { useSubscriptionStore } from '@/lib/subscription-store'

/**
 * Hook that provides the tenant's current effective plan.
 *
 * The subscription store always resolves the tenant's real effective plan once it has
 * successfully fetched - server-side, independent of whether there's an active Stripe/Hotmart
 * subscription behind it (e.g. a plan granted manually via the SaaS Admin console). That's
 * preferred here because it may be fresher than AuthContext's plan, which only updates on
 * login/token validation: an admin plan change made mid-session would otherwise stay locked
 * behind the stale AuthContext plan everywhere except wherever last triggered a subscription
 * fetch. AuthContext's plan is the fallback only until the first successful fetch, for
 * unauthenticated users, or while still loading.
 */
export function useCurrentPlan() {
  const { plan, isAuthenticated, isLoading } = useAuth()
  const subscription = useSubscriptionStore((state) => state.subscription)
  const subscriptionPlan = useSubscriptionStore((state) => state.currentPlan)
  const isSessionInvalid = useSubscriptionStore((state) => state.isSessionInvalid)

  // A 401/403 on the subscription fetch means this session/tenant's access was actively
  // invalidated - that can happen before AuthContext's own isAuthenticated/plan catch up
  // (it only re-validates on its own login/token-validation flow, not on this failure). Once
  // the store has flagged that, AuthContext's plan must not be trusted either, or feature
  // gates would keep granting the stale entitlements the store just determined were invalid.
  const fallbackPlan = isLoading ? plan : (isAuthenticated && !isSessionInvalid ? plan : 'free')
  const effectivePlan = isAuthenticated && !isSessionInvalid && subscription ? subscriptionPlan : fallbackPlan

  return {
    plan: effectivePlan as PlanId,
    isAuthenticated,
    isLoading,
  }
}
